import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { creditApi, CreditBalance } from '../services/creditApi';
import { useAuth } from '../contexts/AuthContext';

export const CreditWarningBanner: React.FC = () => {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (!token) return;

    const load = async () => {
      try {
        const data = await creditApi.getBalance();
        setBalance(data);
      } catch (err) {
        console.error('[CreditWarningBanner] Failed to load balance:', err);
      }
    };
    load();
  }, [token]);

  if (!balance || dismissed) return null;

  // Suspended account — non-dismissable red banner
  if (balance.suspended) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 border-b px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-800 dark:text-red-200 flex-shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-200">
            Your account is suspended due to a failed payment. Please update your payment method to continue processing.{' '}
            <Link to="/account" className="underline font-medium hover:opacity-80">
              Manage billing
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Subscribers are never blocked — no warning banner needed
  if (balance.hasActiveSubscription) return null;

  // Non-subscribers: warn when free pages are running low
  const level = balance.warningLevel || 'NONE';
  if (level === 'NONE') return null;

  const isEarlyAdopter = balance.accountType === 'EARLY_ADOPTER';
  const isDepleted = level === 'DEPLETED' || level === 'CRITICAL';
  const canDismiss = !isDepleted;

  const message = level === 'DEPLETED'
    ? (isEarlyAdopter
        ? "You've used all your free credits. Subscribe to unlock unlimited processing."
        : "You've used all your free pages.")
    : level === 'CRITICAL'
      ? `Only ${balance.freePageBalance} free credits remaining.`
      : `${balance.freePageBalance} free credits remaining.`;

  const bgClass = isDepleted
    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
    : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700';
  const textClass = isDepleted
    ? 'text-red-800 dark:text-red-200'
    : 'text-amber-800 dark:text-amber-200';

  return (
    <div className={`${bgClass} border-b px-4 py-2 flex items-center justify-between`}>
      <div className="flex items-center gap-2">
        <AlertTriangle className={`h-4 w-4 ${textClass} flex-shrink-0`} />
        <p className={`text-sm ${textClass}`}>
          {message}{' '}
          {isDepleted ? (
            <Link
              to="/account"
              className="inline-flex items-center ml-1 px-2.5 py-0.5 rounded bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-colors"
            >
              Subscribe
            </Link>
          ) : (
            <Link to="/account" className="underline font-medium hover:opacity-80">
              View billing
            </Link>
          )}
        </p>
      </div>
      {canDismiss && (
        <button
          onClick={() => setDismissed(true)}
          className={`${textClass} hover:opacity-70 ml-4 flex-shrink-0`}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
